<template>
  <div
    v-if="$parent.inMeeting"
    id="bottomControls"
    :class="{ 'control-is-mobile': $parent.isMobile() }"
    class="fixed-bottom meeting-controls d-flex">
    <volume-indicator
      v-if="$parent.inMeeting"
      ref="miceVolume"
      :display="false"
      :media="$parent.localAudioStream[0].media"
      :mike="mic"
    />
    <div
      id="layout"
      class="mr-auto layoutLauncher control"
      tabindex="0"
      @click="closeToolTip">
      <grid-icon
        v-if="selectedVideoLayout === 0"
        :size="32"/>
      <gesture-tap-icon
        v-if="selectedVideoLayout === 1"
        :size="32" />
      <account-voice-icon
        v-if="selectedVideoLayout === 2"
        :size="32" />
      <no-video-icon
        v-if="selectedVideoLayout === 3"
        :size="32" />
      <limited-icon
        v-if="selectedVideoLayout === 4"
        :size="32" />
      <b-tooltip
        v-if="!$parent.isMobile()"
        target="layout"
        triggers="hover">Switch Layout</b-tooltip>
    </div>
    <div class="m-auto">
      <span
        id="videControl"
        :class="{'middle-controls': !$parent.isMobile() }"
        class="d-inline-block control "
        @click="camera = !camera">
        <video-icon
          v-if="camera"
          id="video"
          :size="32"/>
        <video-off-icon
          v-else
          id="video-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="videControl"
          triggers="hover">{{ toggleText(camera,'Video') }}</b-tooltip>
      </span>
      <span
        id="micControl"
        class="d-inline-block control middle-controls"
        @click="mic = !mic">
        <microphone-icon
          v-if="mic"
          id="mic"
          :size="32"/>
        <microphone-off-icon
          v-else
          id="mic-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="micControl"
          triggers="hover">{{ toggleText(mic,'Me', 'Mute', 'Unmute') }}</b-tooltip>
      </span>
      <span
        id="speakerControl"
        :title="toggleText(speaker,'Audio')"
        :class="{'middle-controls': !$parent.isMobile() }"
        class="d-inline-block control"
        @click="speaker = !speaker">
        <volume-high-icon
          v-if="speaker"
          id="speaker"
          :size="32"/>
        <volume-off-icon
          v-else
          id="speaker-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="speakerControl"
          triggers="hover">{{ toggleText(speaker,'Audio') }}</b-tooltip>
      </span>
      <span
        v-if="!$parent.isMobile() && $parent.isSupportedShare()"
        v-show="desktopControls"
        id="desktopControl"
        class="d-md-inline-block control middle-controls d-none"
        @click="onDesktopChange()">
        <screen-share-icon
          v-if="!desktop"
          id="desktop"
          :size="32"/>
        <screen-share-stop-icon
          v-else
          id="desktop-off"
          :size="32"
          class="muted"/>
        <b-tooltip
          v-if="!$parent.isMobile()"
          target="desktopControl"
          triggers="hover">{{ toggleText(desktop,'Screen', 'Stop', 'Share') }}</b-tooltip>
      </span>
      <span
        id="inMeetingSettings"
        :class="{'middle-controls': !$parent.isMobile() }"
        tabindex="0"
        class="control d-inline-block"
        @click="closeToolTip">
        <dots-horizontal-icon
          v-b-tooltip.hover
          :size="32"/>
      </span>
      <b-tooltip
        v-if="!$parent.isMobile()"
        target="inMeetingSettings"
        triggers="hover">
        Settings
      </b-tooltip>
    </div>
    <span class="control ml-auto danger-hover">
      <exit-to-app-icon
        id="quit"
        :size="32"
        tabindex="0"
        @click="$parent.exitMeeting()"/>
    </span>
    <b-tooltip
      v-if="!$parent.isMobile()"
      target="quit"
      triggers="hover">
      Leave Meeting
    </b-tooltip>

    <b-popover
      ref="meetingSettings"
      target="inMeetingSettings"
      triggers="focus"
      placement="top">
      <ul class="list-unstyled setting-list">
        <li
          class="d-flex position-relative"
          @click="launchSettings('video')">
          <video-icon
            :size="20"
            class="mr-2"/> Video Preferences
        </li>
        <li
          class="d-flex position-relative"
          @click="launchSettings('audio')">
          <microphone-icon
            :size="20"
            class="mr-2 d-flex"/> Audio Preferences
        </li>
      </ul>
    </b-popover>
    <b-popover
      ref="layoutRef"
      custom-class="gridOptions"
      target="layout"
      triggers="focus"
      placement="topright">
      <ul class="list-unstyled setting-list">
        <li
          class="d-flex position-relative"
          @click="changeVideoLayoutOption(0)">
          <span>
            <grid-icon
              :size="20"
              class="mr-2"/>Grid</span>
        </li>
        <li
          class="d-flex position-relative"
          @click="changeVideoLayoutOption(2)">
          <span>
            <account-voice-icon
              :size="20"
              class="mr-2"/>Speaker View</span>
        </li>
        <li
          v-if="!$parent.isMobile()"
          class="d-md-flex position-relative d-none"
          @click="changeVideoLayoutOption(1)">
          <span>
            <gesture-tap-icon
              :size="20"
              class="mr-2"/>Click to Select</span>
        </li>
        <hr>
        <li @click="changeVideoLayoutOption(3)">
          <no-video-icon
            :size="20"
            class="mr-2"/>No Remote Videos
        </li>
      </ul>
    </b-popover>
  </div>
</template>


<script>
import VolumeIndicator from './VolumeIndicator'
import { ref, reactive, watch, onUnmounted, onMounted } from "@vue/composition-api"
import NoVideoIcon from "../icons/NoVideo"
import ExitToAppIcon from "icons/ExitToApp"
import MicrophoneIcon from "icons/Microphone"
import MicrophoneOffIcon from "icons/MicrophoneOff"
import VideoIcon from "icons/Video"
import VideoOffIcon from "icons/VideoOff"
import VolumeHighIcon from "icons/VolumeHigh"
import VolumeOffIcon from "icons/VolumeOff"
import ScreenShareIcon from "../icons/ScreenShare"
import ScreenShareStopIcon from "../icons/ScreenShareStop"
import DotsHorizontalIcon from "icons/DotsHorizontal"
import ChevronDownIcon from "icons/ChevronDown"
import CogIcon from "icons/Cog"
import AccountVoiceIcon from "icons/AccountVoice"
import GestureTapIcon from "icons/GestureTap"
import GridIcon from "../icons/Grid"
import AlertIcon from "icons/Alert"
import LimitedIcon from "../icons/Limited"

export default {
  name: 'MeetingControls',
  components: {
    'volume-indicator': VolumeIndicator,
    'no-video-icon': NoVideoIcon,
    'exit-to-app-icon': ExitToAppIcon,
    'microphone-icon': MicrophoneIcon,
    'microphone-off-icon': MicrophoneOffIcon,
    'video-icon': VideoIcon,
    'video-off-icon': VideoOffIcon,
    'volume-high-icon': VolumeHighIcon,
    'volume-off-icon': VolumeOffIcon,
    'screen-share-icon': ScreenShareIcon,
    'screen-share-stop-icon': ScreenShareStopIcon,
    'dots-horizontal-icon': DotsHorizontalIcon,
    'chevron-down-icon': ChevronDownIcon,
    'cog-icon': CogIcon,
    'account-voice-icon': AccountVoiceIcon,
    'gesture-tap-icon': GestureTapIcon,
    'grid-icon': GridIcon,
    'alert-icon': AlertIcon,
    'limited-icon': LimitedIcon

  },
  props: {
    selectedVideoLayout: {
      type: Number,
      default: 0
    }
  },
  setup(props, context) {
    const selectedShareOption = ref('window')
    const mic = ref(false)
    const speaker = ref(true)
    const miceControls = ref(true)
    const cameraControls = ref(true)
    const camera = ref(false)
    const desktopControls = ref(true)
    const desktop = ref(false)
    const videolayoutControls = ref(true)
    const lockedPanel = ref(true)
    const meetingOptions = ref("")


    function setMeetingOptions() {
      if (meetingOptions.value.includes('sd=0') && desktopControls) {
        desktopControls.value = false
      }
      if (meetingOptions.value.includes('au=0')) {
        if (context.parent.localAudioStream.length > 0 && context.parent.$refs.configDevices.miceOn) {
          context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = false
        }
        mic.value = false
        miceControls.value = false
      } else if (meetingOptions.value.includes('au=4')) {
        if (context.parent.localAudioStream.length > 0 && !context.parent.$refs.configDevices.miceOn) {
          context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = true
        }
        mic.value = true
      } else if (meetingOptions.value.includes('au=3')) {
        if (context.parent.localAudioStream.length > 0 && context.parent.$refs.configDevices.miceOn) {
          context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = false
        }
        mic.value = false
      }
      if (!meetingOptions.value.includes('au=4') && !meetingOptions.value.includes('au=3') && !meetingOptions.value.includes('au=0') && context.parent.$refs.configDevices.miceOn) {
        if (context.parent.localAudioStream.length > 0 && !context.parent.$refs.configDevices.miceOn) {
          context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = true
        }
        mic.value = true
      }
      if (meetingOptions.value.includes('vd=0')) {
        if (context.parent.localVideoStreams.length > 0 && context.parent.$refs.configDevices.cameraOn) {
          context.parent.sendStillImage()
        }
        camera.value = false
        cameraControls.value = false
      } else if (meetingOptions.value.includes('vd=4')) {
        if (context.parent.localVideoStreams.length > 0 && !context.parent.$refs.configDevices.cameraOn) {
          context.parent.changeDevice(context.parent.actualVideoDevice, 'video')
        }
        camera.value = true
      } else if (meetingOptions.value.includes('vd=3')) {
        if (context.parent.localVideoStreams.length > 0 && context.parent.$refs.configDevices.cameraOn) {
          context.parent.sendStillImage()
        }
        camera.value = false
      }
      if (!meetingOptions.value.includes('vd=4') && !meetingOptions.value.includes('vd=3') && !meetingOptions.value.includes('vd=0') && context.parent.$refs.configDevices.cameraOn) {
        if (context.parent.localVideoStreams.length > 0 && !context.parent.$refs.configDevices.cameraOn) {
          context.parent.changeDevice(context.parent.actualVideoDevice, 'video')
        }
        camera.value = true
      }

      if (!meetingOptions.value.includes('ch=12')) { //
        context.parent.$refs.sideBar.$refs.sideChat.selectedChat = "public"

      } else {
        context.parent.plenary = true
      }
      if (meetingOptions.value.includes('dl=0')) {
        videolayoutControls.value = false
        changeVideoLayoutOption(0)
      }
    }

    function changeVideoLayoutOption (id) {
      if (id !== context.parent.videoMode) {
        if (id === 3) {
          // dont send video
          receiveVideo('video-off')
        } else if (id === 4) {
          receiveVideo('video-limited')
        } else {
          // send video
          receiveVideo('video-on')
        }
        context.parent.videoMode = id
      }
      context.refs.layoutRef.$emit('close')
    }

    function getDestination(){
      if (context.root.$route.query.meetingID) {
        if (context.root.$route.query.pin) {
          return (context.root.$route.query.meetingID + context.root.$route.query.pin)
        } else {
          return context.root.$route.query.meetingID
        }
      } else if (context.parent.meetingData.meetingID) {
        if (context.parent.meetingData.pin) {
          return (context.parent.meetingData.meetingID + context.parent.meetingData.pin)
        } else {
          return context.parent.meetingData.meetingID
        }
      } else {
        return
      }
    }

    function receiveVideo (value) {
      let eventHandlers = {
        'succeeded': E => console.log('send message video mode succeded!'),
        'failed': E => console.log('send message video mode failed!')
      }

      let options = {
        'eventHandlers': eventHandlers,
        'extraHeaders': ['Vibe-VideoMode: ' + value]
      }

      let destination = getDestination();
      context.parent.webrtcPhone.sendMessage(destination, '', options)
    }

    function sendControlUpdate(value, type) {
      let isAudio = type === 'mic';
      let output = `${isAudio}/${!value}/${context.parent.meetingData.name}`;
      let options = {
        'extraHeaders': ['Vibe-mute: ' + output]
      }
      let destination = getDestination();
      context.parent.webrtcPhone.sendMessage(destination, '', options)
    }

    function handleModerator(response) {
      var object = response.split('/')
      object = {'isAudio': object[0], 'isMute': object[1]}
      object.isAudio === "true" ? mic.value = !object.isMute : camera.value = !object.isMute;
      var string = object.isAudio === "true" ? 'microphone' : 'camera';
      context.root.$notify.info('A Moderator has turned off your ' + string + '.')
    }

    function plenarySession (status) {
      if (status) {
        cameraControls.value = false
        miceControls.value = false
        desktopControls.value = false
        if (desktop.value) {
          onDesktopChange()
        }
        camera.value = false
        mic.value = false
      } else {
        if (meetingOptions.value.includes('vd=0')) {
          cameraControls.value = false
          camera.value = false
        }
        else if (meetingOptions.value.includes('vd=3')) {
          if (context.parent.localVideoStreams.length > 0 && context.parent.$refs.configDevices.cameraOn) {
            context.parent.sendStillImage()
          }
          cameraControls.value = true
          camera.value = false
        } else {
          cameraControls.value = true
          camera.value = true
        }

        if (meetingOptions.value.includes('au=0')) {
          miceControls.value = false
          mic.value = false
        }
        else if (meetingOptions.value.includes('au=3')) {
          if (context.parent.localAudioStream.length > 0 && context.parent.$refs.configDevices.miceOn) {
            context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = false
          }
          miceControls.value = true
          mic.value = false
        } else {
          miceControls.value = true
          mic.value = true
        }

        desktopControls.value = meetingOptions.value.includes('sd=0') ? false : true
      }
    }

    function broadcast () {
      cameraControls.value = false
      miceControls.value = false
      desktopControls.value = false
    }

    function onDesktopChange () {
      desktop.value = !desktop.value
      if (desktop.value) {
        context.parent.$refs.shareDesktop.shareDesktop()
      } else {
        context.parent.$refs.shareDesktop.stopSharing()
      }
    }

    function toggleText (check, string, verbFrom = 'Stop',verbTo='Start') {
      return (check ? verbFrom : verbTo) + ` ${string}`;
    }



    function toggleVideoList() {
      if (context.parent.videoDevices.length > 1) {
        context.refs.videoOptions.style.display = 'block'
      }
    }

    function toggleAudioList() {
      if (context.parent.audioDevices.length > 1) {
        context.refs.audioOptions.style.display = 'block'
      }
    }

    function launchSettings(pane){
      context.parent.$refs.settings.currentPane = pane;
      this.$bvModal.show('modal-settings');
      context.refs.meetingSettings.$emit('close')
    }

    function toggleSystemMessages(){
      context.parent.$refs.sideBar.toggleBar('system')
      context.refs.meetingSettings.$emit('close')
    }

    function updateSelfPart(value, type) {
      let isAudio = type === 'mic';
      let user = context.parent.$refs.sideBar.$refs.sidePart.participants.find(user => !!user.isSelf);
      if(user) {
        const index = context.parent.$refs.sideBar.$refs.sidePart.participants.indexOf(user)
        if (isAudio) {
          user.isMuted = !value;
        } else {
          user.videoOff = !value;
        }
        context.parent.$refs.sideBar.$refs.sidePart.participants.splice(index, 1, user)  // this is literally a vue caveat
      }
    }

    function closeToolTip(){
      context.root.$emit('bv::hide::tooltip')
    }

    watch(mic, (value, oldValue) => {
      if (oldValue != undefined) {
        updateSelfPart(value,'mic')
        if (value) {
          context.parent.changeDevice(context.parent.actualAudioDevice, 'audio')
        } else {
          context.parent.localAudioStream[0].media.getAudioTracks()[0].enabled = value
        }
        sendControlUpdate(value, 'mic')
      }
    })

    watch(speaker, (value, oldValue) => {
      if (oldValue != undefined) {
        context.parent.remoteAudioStream.getAudioTracks()[0].enabled = value
      }
    })

    watch(desktop, (value, oldValue) => {
      if (oldValue != undefined) {

        if(!camera.value) {
          sendControlUpdate(value, 'camera')
        }
      }
    })

    watch(camera, (value, oldValue) => {
      if (oldValue != undefined) {
        updateSelfPart(value,'camera')
        if (value) {
          context.parent.changeDevice(context.parent.actualVideoDevice, 'video')
        } else {
          context.parent.sendStillImage()
        }
        if (context.parent.maxBw > 0) {
          context.parent.setMaxBw(context.parent.maxBw)
        }
        if(!desktop.value) {
          sendControlUpdate(value, 'camera')
        }
      }
    })
    return {
      selectedShareOption,
      changeVideoLayoutOption,
      mic,
      speaker,
      miceControls,
      cameraControls,
      camera,
      desktopControls,
      desktop,
      plenarySession,
      broadcast,
      onDesktopChange,
      videolayoutControls,
      lockedPanel,
      setMeetingOptions,
      toggleVideoList,
      toggleAudioList,
      meetingOptions,
      sendControlUpdate,
      toggleText,
      launchSettings,
      closeToolTip,
      handleModerator,
      toggleSystemMessages,
    }
  }
}
</script>
<style>
  .control-is-mobile:hover span.material-design-icon { background-color: transparent !important; }

</style>



// WEBPACK FOOTER //
// src/components/MeetingControls.vue